import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import DescriptionView from './DescriptionView';

const BRAND = '#500000';
export const eur = (v) => `€${Number(v || 0).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty = (q) => Number(q || 0).toLocaleString('nl-BE', { maximumFractionDigits: 2 });
const fmtUnit = (u) => (['m2', 'vierkante meter'].includes(u) ? 'm²' : ['lm', 'lopende meter'].includes(u) ? 'm' : u || '');

const openMemory = {};

const PostRow = ({ row }) => (
  <div data-testid={`portal-post-${row.id}`} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6 py-4 border-b border-gray-100 last:border-b-0">
    <div className="flex-1 min-w-0 text-sm text-gray-800 leading-relaxed">
      <DescriptionView text={row.description} />
      {row.discount_percent > 0 && (
        <span className="inline-block mt-1 text-xs font-semibold text-emerald-600">-{row.discount_percent}% korting</span>
      )}
    </div>
    <div className="flex sm:flex-col sm:items-end gap-3 sm:gap-1 shrink-0 text-sm sm:min-w-[120px]">
      {fmtUnit(row.unit) && (
        <span data-testid={`portal-post-qty-${row.id}`} className="text-gray-600 whitespace-nowrap">{fmtQty(row.quantity)} {fmtUnit(row.unit)}</span>
      )}
      {row.price_excl_vat !== null && row.price_excl_vat !== undefined && (
        <span data-testid={`portal-post-price-${row.id}`} className="font-semibold text-gray-900 whitespace-nowrap">
          {eur(row.price_excl_vat)}{row.vat_rate != null && <span className="ml-1 text-xs font-normal text-gray-400">({row.vat_rate}% btw)</span>}
        </span>
      )}
    </div>
  </div>
);

const SubtotalRow = ({ row }) => (
  <div data-testid={`portal-subtotal-${row.id}`} className="flex items-center justify-between my-2 px-4 py-2.5 rounded-md text-sm font-semibold" style={{ backgroundColor: '#f5e6e6', color: BRAND }}>
    <span className="min-w-0">{row.label}</span>
    <span className="shrink-0 whitespace-nowrap ml-3 flex flex-col items-end leading-tight">{eur(row.amount)} <span className="text-[11px] font-normal opacity-70">excl. BTW</span></span>
  </div>
);

const Section = ({ section, idx, open, onToggle, testPrefix }) => {
  const posts = section.rows.filter((r) => r.kind === 'item').length;
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`${testPrefix}-section-${idx}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`${testPrefix}-section-toggle-${idx}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-white transition-opacity hover:opacity-95"
        style={{ backgroundColor: BRAND }}
      >
        <div className="min-w-0">
          <p className="font-bold uppercase tracking-wide text-sm sm:text-base truncate">{section.title}</p>
          <p className="text-xs opacity-75">{posts} {posts === 1 ? 'post' : 'posten'}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span data-testid={`${testPrefix}-section-total-${idx}`} className="font-semibold text-sm sm:text-base">{eur(section.total)}</span>
          <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 py-1 bg-white" data-testid={`${testPrefix}-section-body-${idx}`}>
          {section.rows.map((row) => (row.kind === 'subtotal' ? <SubtotalRow key={row.id} row={row} /> : <PostRow key={row.id} row={row} />))}
        </div>
      )}
    </div>
  );
};

export const PortalQuoteSections = ({ quoteId, sections = [] }) => {
  const [openSet, setOpenRaw] = useState(() => openMemory[quoteId] || new Set());
  const setOpenSet = (fn) => setOpenRaw((prev) => {
    const next = typeof fn === 'function' ? fn(prev) : fn;
    openMemory[quoteId] = next;
    return next;
  });
  const testPrefix = `portal-quote-${quoteId}`;
  if (!sections.length) return <p className="text-sm text-gray-500">Nog geen posten in deze offerte.</p>;
  const allOpen = openSet.size === sections.length;
  const toggle = (i) => setOpenSet((prev) => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          type="button"
          data-testid={`${testPrefix}-toggle-all`}
          onClick={() => setOpenSet(allOpen ? new Set() : new Set(sections.map((_, i) => i)))}
          className="text-sm font-semibold underline underline-offset-4 hover:opacity-80"
          style={{ color: BRAND }}
        >
          {allOpen ? 'Alles dichtklappen' : 'Alles openklappen'}
        </button>
      </div>
      {sections.map((s, i) => (
        <Section key={i} section={s} idx={i} open={openSet.has(i)} onToggle={() => toggle(i)} testPrefix={testPrefix} />
      ))}
    </div>
  );
};
